import React, { useMemo } from 'react';
import { Variable, AlertCircle, MessageSquare } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type PromptMode = 'append' | 'override' | 'variable';

export interface PromptConfig {
  mode: PromptMode;
  targetVariable?: string; // Only used when mode === 'variable'
}

interface PlaygroundInputProps {
  agentContent: string;
  inputVariables: Record<string, string>;
  onVariablesChange: (variables: Record<string, string>) => void;
  userPrompt?: string;
  onUserPromptChange?: (prompt: string) => void;
  promptConfig: PromptConfig;
  onPromptConfigChange: (config: PromptConfig) => void;
  disabled?: boolean;
  showUserPrompt?: boolean; // Default true - set to false for interactive mode
}

// Detect Jinja2 template variables from content
// Handles: {{ query }}, {{ user_input }}, {{ context.data }}, etc.
function detectVariables(content: string): string[] {
  // Match {{ variable }} and {{ variable.property }} patterns
  const pattern = /\{\{\s*([\w.]+)\s*\}\}/g;
  const matches = [...content.matchAll(pattern)];

  // Get unique root variable names (e.g., "context" from "context.data")
  const variables = new Set<string>();
  for (const match of matches) {
    const fullVar = match[1];
    // Get the root variable name (before any dot)
    const rootVar = fullVar.split('.')[0];
    variables.add(rootVar);
  }

  // Filter out common reserved/built-in names
  const reserved = ['loop', 'self', 'super', 'true', 'false', 'none', 'True', 'False', 'None'];
  return [...variables].filter(v => !reserved.includes(v));
}

export function PlaygroundInput({
  agentContent,
  inputVariables,
  onVariablesChange,
  userPrompt = '',
  onUserPromptChange,
  promptConfig,
  onPromptConfigChange,
  disabled = false,
  showUserPrompt = true,
}: PlaygroundInputProps) {
  // Detect template variables from agent content
  const detectedVariables = useMemo(() => {
    return detectVariables(agentContent);
  }, [agentContent]);

  // Variables that need manual input (excludes the one used for prompt in variable mode)
  const requiredVariables = useMemo(() => {
    if (promptConfig.mode === 'variable' && promptConfig.targetVariable) {
      return detectedVariables.filter(v => v !== promptConfig.targetVariable);
    }
    return detectedVariables;
  }, [detectedVariables, promptConfig]);

  const handleVariableChange = (name: string, value: string) => {
    onVariablesChange({
      ...inputVariables,
      [name]: value,
    });
  };

  const handleModeChange = (mode: PromptMode) => {
    if (mode === 'variable' && detectedVariables.length > 0) {
      // Default to first variable when switching to variable mode
      onPromptConfigChange({
        mode,
        targetVariable: promptConfig.targetVariable || detectedVariables[0],
      });
    } else {
      onPromptConfigChange({ mode });
    }
  };

  const handleTargetVariableChange = (variable: string) => {
    onPromptConfigChange({
      mode: 'variable',
      targetVariable: variable,
    });
  };

  // Check which required variables are missing values
  const missingVariables = requiredVariables.filter(v => !inputVariables[v]?.trim());

  const hasVariables = detectedVariables.length > 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Prompt Mode Selector - only show in one-shot mode with user prompt */}
      {showUserPrompt && onUserPromptChange && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500" />
            <Label className="text-sm font-medium">Prompt Mode</Label>
          </div>
          <RadioGroup
            value={promptConfig.mode}
            onValueChange={(v) => handleModeChange(v as PromptMode)}
            className="space-y-2"
            disabled={disabled}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="append" id="mode-append" />
              <Label htmlFor="mode-append" className="text-sm font-normal cursor-pointer">
                Append to agent body
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="override" id="mode-override" />
              <Label htmlFor="mode-override" className="text-sm font-normal cursor-pointer">
                Override agent body
              </Label>
            </div>
            {hasVariables && (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="variable" id="mode-variable" />
                <Label htmlFor="mode-variable" className="text-sm font-normal cursor-pointer flex items-center gap-2">
                  Fill variable:
                  {promptConfig.mode === 'variable' && (
                    <Select
                      value={promptConfig.targetVariable}
                      onValueChange={handleTargetVariableChange}
                      disabled={disabled}
                    >
                      <SelectTrigger className="h-7 w-32 text-xs">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {detectedVariables.map((v) => (
                          <SelectItem key={v} value={v} className="text-xs font-mono">
                            {`{{ ${v} }}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Label>
              </div>
            )}
          </RadioGroup>
        </div>
      )}

      {/* Template Variables - only show required ones */}
      {requiredVariables.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Variable className="h-4 w-4 text-purple-500" />
              <Label className="text-sm font-medium">
                {promptConfig.mode === 'variable' ? 'Required Variables' : 'Template Variables'}
              </Label>
            </div>
            <div className="flex items-center gap-2">
              {missingVariables.length > 0 && (
                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {missingVariables.length} missing
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs">
                {requiredVariables.length} required
              </Badge>
            </div>
          </div>
          <ScrollArea className="max-h-48">
            <div className="space-y-3 pr-4">
              {requiredVariables.map((variable) => {
                const isMissing = !inputVariables[variable]?.trim();
                return (
                  <div key={variable} className="space-y-1">
                    <Label
                      className={cn(
                        "text-xs font-mono",
                        isMissing ? "text-orange-600" : "text-muted-foreground"
                      )}
                    >
                      {"{{ "}{variable}{" }}"}
                      {isMissing && <span className="text-orange-500 ml-1">*</span>}
                    </Label>
                    <Input
                      value={inputVariables[variable] || ''}
                      onChange={(e) => handleVariableChange(variable, e.target.value)}
                      placeholder={`Enter value for ${variable}...`}
                      className={cn(
                        "h-9 text-sm font-mono",
                        isMissing && "border-orange-300 focus-visible:ring-orange-400"
                      )}
                      disabled={disabled}
                    />
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          {/* Show note about which variable is filled by prompt */}
          {promptConfig.mode === 'variable' && promptConfig.targetVariable && (
            <p className="text-xs text-muted-foreground italic">
              Note: <code className="font-mono">{`{{ ${promptConfig.targetVariable} }}`}</code> will be filled by your prompt below
            </p>
          )}
        </div>
      )}

      {/* User Prompt - only shown in one-shot mode */}
      {showUserPrompt && onUserPromptChange && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground">
            User Prompt
          </Label>
          <Textarea
            value={userPrompt}
            onChange={(e) => onUserPromptChange(e.target.value)}
            placeholder={
              promptConfig.mode === 'variable' && promptConfig.targetVariable
                ? `Enter value for {{ ${promptConfig.targetVariable} }}...`
                : "Enter your prompt to send to the agent..."
            }
            className="min-h-24 resize-none text-sm"
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground">
            {promptConfig.mode === 'append' && 'This will be appended after the agent body content.'}
            {promptConfig.mode === 'override' && 'This will replace the agent body content.'}
            {promptConfig.mode === 'variable' && promptConfig.targetVariable &&
              `This will fill the {{ ${promptConfig.targetVariable} }} variable.`}
          </p>
        </div>
      )}
    </div>
  );
}

export default PlaygroundInput;
